import { useState, useEffect } from "react";
import { useIsMobile } from "./hooks/useIsMobile";
import FinanceDashboard from "./components/FinanceDashboard";
import LandingPage from "./components/LandingPage";

const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

// ─── App — auth guard + root router ──────────────────────────────────────────
export default function App() {
  const isMobile = useIsMobile();
  const [token,       setToken]       = useState(null);
  const [checking,    setChecking]    = useState(true);
  // Email verification overlay state
  const [verifyState,   setVerifyState]   = useState(null); // null | 'pending' | 'success' | 'error'
  const [verifyMessage, setVerifyMessage] = useState("");
  // Password reset overlay state
  const [resetToken,      setResetToken]      = useState(null);  // raw token from URL
  const [resetPassword,   setResetPassword]   = useState("");
  const [resetConfirm,    setResetConfirm]    = useState("");
  const [resetState,      setResetState]      = useState(null);  // null | 'form' | 'loading' | 'success' | 'error'
  const [resetMessage,    setResetMessage]    = useState("");

  useEffect(() => {
    // 1. Check URL for ?verify_token= (from email link)
    const params = new URLSearchParams(window.location.search);
    const verifyToken = params.get("verify_token");

    if (verifyToken) {
      // Strip token from URL immediately
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("verify_token");
      window.history.replaceState({}, "", cleanUrl.toString());

      setVerifyState("pending");
      fetch(`${API}/api/v1/auth/verify-email?token=${encodeURIComponent(verifyToken)}`)
        .then(r => r.json())
        .then(d => {
          if (d.message) {
            setVerifyState("success");
            setVerifyMessage(d.message);
          } else {
            setVerifyState("error");
            setVerifyMessage(d.error || "Verification failed.");
          }
        })
        .catch(() => {
          setVerifyState("error");
          setVerifyMessage("Network error. Please try again.");
        })
        .finally(() => setChecking(false));
      return;
    }

    // 1b. Check URL for ?reset_token= (from password reset email)
    const rawResetToken = params.get("reset_token");
    if (rawResetToken) {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("reset_token");
      window.history.replaceState({}, "", cleanUrl.toString());
      setResetToken(rawResetToken);
      setResetState("form");
      setChecking(false);
      return;
    }

    // 2. No verify token — try to restore session from httpOnly cookie
    fetch(`${API}/api/v1/auth/refresh`, {
      method: "POST",
      credentials: "include",
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.accessToken) setToken(d.accessToken); })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  const handleLogout = () => {
    fetch(`${API}/api/v1/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => {});
    setToken(null);
  };

  const handleResetSubmit = () => {
    if (!resetPassword || resetPassword !== resetConfirm) {
      setResetMessage("Passwords do not match.");
      return;
    }
    if (resetPassword.length < 8) {
      setResetMessage("Password must be at least 8 characters.");
      return;
    }
    if (!/[A-Z]/.test(resetPassword)) {
      setResetMessage("Password must contain at least one uppercase letter.");
      return;
    }
    if (!/[0-9]/.test(resetPassword)) {
      setResetMessage("Password must contain at least one number.");
      return;
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(resetPassword)) {
      setResetMessage("Password must contain at least one special character.");
      return;
    }
    setResetState("loading");
    setResetMessage("");
    fetch(`${API}/api/v1/auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: resetToken, password: resetPassword }),
    })
      .then(r => r.json())
      .then(d => {
        if (d.ok) {
          setResetState("success");
        } else {
          setResetMessage(d.errors?.[0]?.msg || d.message || d.error || "Reset failed.");
          setResetState("form");
        }
      })
      .catch(() => { setResetMessage("Network error. Please try again."); setResetState("form"); });
  };

  // ── Password reset overlay ────────────────────────────────────────────────
  if (resetState === "form" || resetState === "loading" || resetState === "success") {
    const isLoading = resetState === "loading";
    const isSuccess = resetState === "success";
    const inp2 = { width: "100%", padding: "12px 14px", background: "var(--bg-main)", border: "1px solid var(--border-strong)", borderRadius: "10px", color: "var(--text-primary)", fontSize: "14px", fontFamily: "'Capsule Sans', sans-serif", outline: "none", boxSizing: "border-box" };
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg-main)", color: "var(--text-primary)", fontFamily: "'Capsule Sans', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
        <div style={{ maxWidth: "420px", width: "100%", background: "var(--bg-card)", border: "1px solid var(--border-strong)", borderRadius: "20px", padding: isMobile ? "28px 20px" : "40px 36px", textAlign: "center", boxShadow: "0 24px 64px rgba(0,0,0,0.6)" }}>
          <div style={{ width: "52px", height: "52px", borderRadius: "14px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px", margin: "0 auto 20px" }}>◈</div>
          {isSuccess ? (
            <>
              <div style={{ fontSize: "36px", marginBottom: "12px" }}>✅</div>
              <div style={{ fontSize: "22px", fontWeight: "700", marginBottom: "10px" }}>Password updated!</div>
              <div style={{ color: "var(--text-muted)", fontSize: "14px", marginBottom: "28px" }}>Your password has been reset. You can now sign in with your new password.</div>
              <button
                onClick={() => { setResetState(null); setResetToken(null); setResetPassword(""); setResetConfirm(""); }}
                style={{ width: "100%", padding: "13px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", border: "none", borderRadius: "10px", color: "#0D1410", fontWeight: "700", fontSize: "15px", cursor: "pointer" }}
              >Sign In →</button>
            </>
          ) : (
            <>
              <div style={{ fontSize: "22px", fontWeight: "700", marginBottom: "8px" }}>Set new password</div>
              <div style={{ color: "var(--text-muted)", fontSize: "13px", marginBottom: "16px" }}>Choose a strong password for your account.</div>
              {/* Password requirements box */}
              <div style={{ background: "var(--bg-elevated)", border: "1px solid var(--border-strong)", borderRadius: "8px", padding: "10px 14px", marginBottom: "16px", textAlign: "left" }}>
                <div style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace", marginBottom: "6px", letterSpacing: "0.08em" }}>PASSWORD REQUIREMENTS</div>
                {[
                  { test: resetPassword.length >= 8,                            label: "At least 8 characters" },
                  { test: /[A-Z]/.test(resetPassword),                          label: "One uppercase letter (A–Z)" },
                  { test: /[0-9]/.test(resetPassword),                          label: "One number (0–9)" },
                  { test: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(resetPassword), label: "One special character (!@#$…)" },
                ].map(({ test, label }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
                    <span style={{ fontSize: "12px", color: test ? "#6fcf6f" : "#888", transition: "color 0.2s" }}>{test ? "✓" : "○"}</span>
                    <span style={{ fontSize: "12px", color: test ? "#9ae09a" : "#aaa", fontFamily: "'JetBrains Mono', monospace", transition: "color 0.2s" }}>{label}</span>
                  </div>
                ))}
              </div>
              {resetMessage && (
                <div style={{ background: "#2d1a1a", border: "1px solid #5a2a2a", borderRadius: "8px", padding: "10px 14px", color: "#e07070", fontSize: "13px", marginBottom: "16px" }}>{resetMessage}</div>
              )}
              <div style={{ textAlign: "left", marginBottom: "14px" }}>
                <label style={{ display: "block", fontSize: "11px", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "8px", fontFamily: "'JetBrains Mono', monospace" }}>New password</label>
                <input type="password" placeholder="Enter new password" value={resetPassword} onChange={e => setResetPassword(e.target.value)} style={inp2} />
              </div>
              <div style={{ textAlign: "left", marginBottom: "20px" }}>
                <label style={{ display: "block", fontSize: "11px", color: "var(--text-muted)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "8px", fontFamily: "'JetBrains Mono', monospace" }}>Confirm password</label>
                <input type="password" placeholder="Repeat password" value={resetConfirm} onChange={e => setResetConfirm(e.target.value)} onKeyDown={e => e.key === "Enter" && handleResetSubmit()} style={inp2} />
              </div>
              <button
                onClick={handleResetSubmit} disabled={isLoading}
                style={{ width: "100%", padding: "13px", background: "linear-gradient(135deg, #D4AF37, #9A7C1E)", border: "none", borderRadius: "10px", color: "#0D1410", fontWeight: "700", fontSize: "15px", cursor: isLoading ? "not-allowed" : "pointer", opacity: isLoading ? 0.7 : 1 }}
              >
                {isLoading ? "Updating…" : "Update Password →"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Email verification overlay ────────────────────────────────────────────
  if (verifyState === "pending" || verifyState === "success" || verifyState === "error") {
    const isSuccess = verifyState === "success";
    const isPending = verifyState === "pending";
    return (
      <div style={{
        minHeight: "100vh", background: "var(--bg-main)", color: "var(--text-primary)",
        fontFamily: "'Capsule Sans', sans-serif",
        display: "flex", alignItems: "center", justifyContent: "center", padding: "24px",
      }}>
        <div style={{
          maxWidth: "440px", width: "100%",
          background: "var(--bg-card)", border: "1px solid var(--border-strong)",
          borderRadius: "20px", padding: isMobile ? "28px 20px" : "40px 36px",
          textAlign: "center", boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
        }}>
          {/* Logo */}
          <div style={{
            width: "52px", height: "52px", borderRadius: "14px",
            background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "24px", margin: "0 auto 16px",
          }}>◈</div>

          {isPending && (
            <>
              <div style={{ fontSize: "22px", fontWeight: "700", marginBottom: "10px" }}>Verifying…</div>
              <div style={{ color: "var(--text-muted)", fontSize: "14px" }}>Just a moment.</div>
            </>
          )}
          {isSuccess && (
            <>
              <div style={{ fontSize: "36px", marginBottom: "12px" }}>✅</div>
              <div style={{ fontSize: "22px", fontWeight: "700", marginBottom: "10px" }}>Email verified!</div>
              <div style={{ color: "var(--text-muted)", fontSize: "14px", marginBottom: "28px" }}>{verifyMessage}</div>
              <button
                onClick={() => { setVerifyState(null); setVerifyMessage(""); }}
                style={{
                  width: "100%", padding: "13px",
                  background: "linear-gradient(135deg, #D4AF37, #9A7C1E)",
                  border: "none", borderRadius: "10px", color: "#0D1410",
                  fontWeight: "700", fontSize: "15px", cursor: "pointer",
                }}
              >Sign In →</button>
            </>
          )}
          {verifyState === "error" && (
            <>
              <div style={{ fontSize: "36px", marginBottom: "12px" }}>⚠️</div>
              <div style={{ fontSize: "22px", fontWeight: "700", marginBottom: "10px" }}>Verification failed</div>
              <div style={{ color: "var(--text-muted)", fontSize: "14px", marginBottom: "28px" }}>{verifyMessage}</div>
              <button
                onClick={() => { setVerifyState(null); setVerifyMessage(""); }}
                style={{
                  width: "100%", padding: "13px",
                  background: "#1e1d16", border: "1px solid var(--border-strong)",
                  borderRadius: "10px", color: "var(--text-primary)",
                  fontWeight: "600", fontSize: "14px", cursor: "pointer",
                }}
              >Back to Sign In</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Loading / auth check ──────────────────────────────────────────────────
  if (checking) {
    return (
      <div style={{
        minHeight: "100vh", background: "var(--bg-main)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{ color: "var(--text-muted)", fontFamily: "monospace", fontSize: "13px" }}>
          Authenticating…
        </div>
      </div>
    );
  }

  if (!token) {
    return <LandingPage onLogin={setToken} />;
  }

  return <FinanceDashboard token={token} onLogout={handleLogout} />;
}
